import { Button, toast, Typography, useMediaQuery } from "@kyan/ui";

import { InstrumentSummary } from "#/components/InstrumentSummary/InstrumentSummary";
import { useCancelOrders } from "#/services/kyan/hooks/useCancelOrders";
import { type TradeDirection } from "#/types/trade";
import { isBuy } from "#/utils/marketActionConverter";
import { confirm } from "#/utils/modals/confirm";
import { formatCurrency } from "#/utils/valueFormatters";

interface CancelOrderProps {
  size: number;
  orderId: string;
  direction: TradeDirection;
  instrument: string;
  limitPrice: number;
  isLiquidated: boolean;
}

export const CancelOrder: React.FC<CancelOrderProps> = ({
  size,
  orderId,
  direction,
  instrument,
  limitPrice,
  isLiquidated,
}) => {
  const { isSm } = useMediaQuery();
  const { mutate, isPending } = useCancelOrders({
    onSuccess: () => {
      toast.success({ title: "Order cancelled successfully!" });
    },
    onError: (error) => {
      toast.error({
        title: "Error cancelling order",
        description: error.message,
      });
    },
  });

  const handleCancelOrder = () => {
    confirm({
      header: "Are you sure you want to cancel this order?",
      content: (
        <div className="mt-6.5 grid gap-3">
          <InstrumentSummary
            size={String(size)}
            isBuy={isBuy(direction)}
            instrumentName={instrument}
            className="border-divider border bg-white dark:bg-black dark:bg-[linear-gradient(180deg,rgba(113,113,113,0.30)_-78.41%,rgba(184,184,184,0.00)_100%)] dark:shadow-[0_0_12px_0_rgba(0,0,0,0.50)_inset]"
          />

          <Typography element="p" className="flex items-center justify-between">
            Limit Price
            <Typography grotesk primary>
              {formatCurrency(limitPrice)}
            </Typography>
          </Typography>
        </div>
      ),
      cancelText: "No, keep it",
      confirmText: "Yes, cancel",
      onConfirm: () => {
        mutate({ order_ids: [orderId] });
      },
    });
  };

  if (isLiquidated)
    return <Typography className="text-error">Liquidation</Typography>;

  return (
    <Button
      size="sm"
      isLoading={isPending}
      variant={isSm ? "action" : "primary"}
      onClick={handleCancelOrder}
    >
      Cancel
    </Button>
  );
};
